import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Link } from "react-router-dom";
import Counter from "@/components/Counter";
import LiveDashboardMock from "@/components/LiveDashboard";
import VideoModal from "@/components/VideoModal";
import { API } from "@/lib/api";

const sensors = [
  { icon: "air", label: "AIR" },
  { icon: "water_drop", label: "WATER" },
  { icon: "volume_up", label: "NOISE" },
  { icon: "factory", label: "INDUSTRIAL" },
  { icon: "directions_boat", label: "MARITIME" },
];

const containerStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

function LiveSparkline() {
  // Generates a flowing live sparkline that updates every 1.2s
  const [points, setPoints] = useState(() =>
    Array.from({ length: 36 }, (_, i) => 30 + Math.sin(i / 3) * 14 + Math.random() * 8)
  );
  useEffect(() => {
    const id = setInterval(() => {
      setPoints((prev) => {
        const next = [...prev.slice(1), Math.max(8, Math.min(58, prev[prev.length - 1] + (Math.random() - 0.5) * 10))];
        return next;
      });
    }, 1200);
    return () => clearInterval(id);
  }, []);

  const w = 320;
  const h = 80;
  const step = w / (points.length - 1);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - p}`).join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
      <defs>
        <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#90B849" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#90B849" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark)" />
      <path d={path} fill="none" stroke="#90B849" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(points.length - 1) * step} cy={h - points[points.length - 1]} r="3" fill="#90B849">
        <animate attributeName="r" values="3;6;3" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function LiveNumber({ base, drift, decimals = 0, suffix = "" }) {
  const [v, setV] = useState(base);
  useEffect(() => {
    const id = setInterval(() => {
      setV((prev) => {
        const next = prev + (Math.random() - 0.5) * drift;
        return Math.max(0, next);
      });
    }, 1500);
    return () => clearInterval(id);
  }, [drift]);
  return (
    <span className="font-mono tabular-nums">
      {v.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const tickerItems = [
  { code: "PM2.5", base: 18.4, drift: 2.8, suffix: " µg/m³", site: "Lagos · Apapa" },
  { code: "NO₂", base: 24.1, drift: 3.2, suffix: " ppb", site: "Nairobi · Industrial Zn" },
  { code: "SO₂", base: 6.7, drift: 1.4, suffix: " ppb", site: "Accra · Tema Port" },
  { code: "CO", base: 0.42, drift: 0.08, suffix: " ppm", site: "Dar es Salaam · CBD" },
  { code: "O₃", base: 32.5, drift: 4.2, suffix: " ppb", site: "Kampala · Kawempe" },
  { code: "TSS", base: 41.2, drift: 5.1, suffix: " mg/L", site: "Mombasa · Effluent" },
  { code: "pH", base: 7.2, drift: 0.2, suffix: "", site: "Lagos · Outfall 3" },
  { code: "Leq", base: 64.7, drift: 2.4, suffix: " dB", site: "Tema · Stack 4" },
];

function DataTicker() {
  // Continuous marquee — duplicate items for seamless loop
  const stream = [...tickerItems, ...tickerItems];
  return (
    <div className="relative overflow-hidden border-y border-brand/20 bg-black/30 backdrop-blur-sm">
      <div className="flex gap-12 marquee whitespace-nowrap py-3">
        {stream.map((t, i) => (
          <div key={i} className="flex items-center gap-3 text-[11px] uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            <span className="text-brand font-bold">{t.code}</span>
            <span className="text-white">
              <LiveNumber base={t.base} drift={t.drift} decimals={t.code === "pH" || t.code === "CO" ? 2 : 1} suffix={t.suffix} />
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400 normal-case tracking-normal">{t.site}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const HERO_PHRASES_FALLBACK = [
  "measured in real-time.",
  "monitored 24/7.",
  "quantified with precision.",
  "verified by regulators.",
  "governed transparently.",
];

function RotatingHeadline() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);

  // Pull phrases from i18n; fall back to English if anything goes wrong.
  let phrases = t("home.hero.rotating", { returnObjects: true });
  if (!Array.isArray(phrases) || phrases.length === 0) {
    phrases = HERO_PHRASES_FALLBACK;
  }

  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % phrases.length);
    }, 3000);
    return () => clearInterval(id);
  }, [phrases.length]);

  return (
    <span className="relative inline-block align-bottom">
      <AnimatePresence mode="wait">
        <motion.span
          key={`${idx}-${phrases[idx]}`}
          initial={{ opacity: 0, y: 26, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -18, filter: "blur(6px)" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-rotating-phrase"
          className="inline-block bg-clip-text text-transparent hero-shimmer bg-[linear-gradient(120deg,#90B849_0%,#6EE7B7_30%,#5EEAD4_55%,#A7F3D0_75%,#90B849_100%)] bg-[length:220%_100%]"
        >
          {phrases[idx]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function HeroHome({ onWatchVideo }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const yMid = useTransform(scrollY, [0, 600], [0, 80]);
  const { t } = useTranslation();

  return (
    <header
      ref={ref}
      data-testid="hero-home"
      className="relative pt-28 overflow-hidden bg-brand-dark text-white"
    >
      {/* Dark forest gradient background — no pink, no peach */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(144,184,73,0.20),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(45,212,191,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/60 via-brand-dark to-brand-dark" />
        <div className="absolute inset-0 tech-grid-dark opacity-50" />
      </div>

      {/* Floating green orbs */}
      <motion.div style={{ y: yMid }} className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand/15 blur-3xl pointer-events-none" />
      <motion.div style={{ y: yMid }} className="absolute top-60 -left-32 w-[360px] h-[360px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

      <div className="relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 pt-12 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left — Text */}
          <motion.div initial="hidden" animate="show" variants={containerStagger} className="lg:col-span-7">
            <motion.div variants={item} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[11px] font-bold uppercase tracking-widest mb-8">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
              </span>
              {t("home.hero.eyebrow")}
            </motion.div>

            <motion.h1 variants={item} className="font-h1 text-[42px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-tight font-extrabold mb-6">
              {t("home.hero.anchor")}{" "}
              <RotatingHeadline />
            </motion.h1>

            <motion.p variants={item} className="text-lg md:text-xl text-zinc-300 max-w-xl mb-3">
              {t("home.hero.subtitle")}
            </motion.p>

            <motion.p variants={item} className="font-manrope text-sm italic text-brand/80 max-w-xl mb-10">
              {t("home.hero.quote")}
            </motion.p>

            <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/contact"
                data-testid="hero-request-demo"
                className="group px-7 py-4 bg-brand text-brand-dark font-bold rounded-xl shadow-[0_0_40px_-8px_rgba(144,184,73,0.6)] hover:shadow-[0_0_60px_-4px_rgba(144,184,73,0.8)] hover:-translate-y-0.5 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                {t("nav.request_demo")}
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </Link>
              <Link
                to="/solutions"
                data-testid="hero-partner-with-us"
                className="px-7 py-4 bg-white/5 text-white border border-white/15 backdrop-blur font-bold rounded-xl hover:bg-white/10 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                {t("home.hero.cta_primary")}
              </Link>
              <button
                type="button"
                onClick={onWatchVideo}
                data-testid="hero-watch-film"
                className="group px-7 py-4 bg-transparent text-white border border-brand/40 font-bold rounded-xl hover:bg-brand/10 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <span className="w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined icon-fill text-[16px]">play_arrow</span>
                </span>
                {t("home.hero.cta_video")}
              </button>
            </motion.div>

            {/* Trust strip */}
            <motion.div variants={item} className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6 border-t border-white/10">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t("home.hero.deployed_with")}</span>
              {[
                { c: "GOCEMS", country: "Ghana" },
                { c: "KOCEMS", country: "Kenya" },
                { c: "TOCEMS", country: "Tanzania" },
                { c: "NIMACEMS", country: "Nigeria" },
              ].map((b) => (
                <div key={b.c} className="flex items-center gap-1.5">
                  <span className="text-brand font-bold text-sm">{b.c}</span>
                  <span className="text-zinc-500 text-xs">· {b.country}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right — Animated console */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-brand/25 bg-zinc-950/80 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              {/* Console header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-950/80">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-brand" />
                  <span className="ml-3 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{t("home_extra.console.live_label")}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-brand font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                  {t("home_extra.console.streaming")}
                </div>
              </div>

              {/* Console body */}
              <div className="p-5 space-y-5">
                {/* Featured reading */}
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{t("home_extra.console.live_aqi")}</div>
                    <div className="font-mono text-5xl font-extrabold text-white tabular-nums">
                      <LiveNumber base={62.4} drift={4.2} decimals={1} />
                    </div>
                    <div className="text-xs text-brand mt-1 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">trending_down</span>
                      {t("home_extra.console.trend")}
                    </div>
                  </div>
                  <div className="px-2.5 py-1 rounded-md bg-brand/15 border border-brand/30 text-[10px] uppercase tracking-widest text-brand font-bold">
                    {t("home_extra.console.healthy")}
                  </div>
                </div>

                {/* Sparkline */}
                <div>
                  <div className="flex justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    <span>{t("home_extra.console.pm_window")}</span>
                    <span className="text-brand">+0.4σ</span>
                  </div>
                  <LiveSparkline />
                </div>

                {/* Pollutant grid */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { l: "NO₂", b: 24.1, d: 3, dec: 1, u: "ppb" },
                    { l: "SO₂", b: 6.7, d: 1.4, dec: 1, u: "ppb" },
                    { l: "CO", b: 0.42, d: 0.08, dec: 2, u: "ppm" },
                    { l: "O₃", b: 32.5, d: 4.2, dec: 1, u: "ppb" },
                    { l: "TSS", b: 41.2, d: 5.1, dec: 1, u: "mg/L" },
                    { l: "Leq", b: 64.7, d: 2.4, dec: 1, u: "dB" },
                  ].map((p) => (
                    <div key={p.l} className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-2.5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{p.l}</div>
                      <div className="font-mono text-sm text-white mt-0.5">
                        <LiveNumber base={p.b} drift={p.d} decimals={p.dec} />
                      </div>
                      <div className="text-[9px] text-zinc-500 mt-0.5">{p.u}</div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                    2000+ {t("home_extra.console.sensors_label")}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{t("home_extra.console.version")}</span>
                </div>
              </div>
            </div>

            {/* Floating badges around console */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="absolute -top-4 -right-4 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full glass-dark text-xs font-bold text-brand border border-brand/30 shadow-lg"
            >
              <span className="material-symbols-outlined text-[14px]">verified</span>
              ISO 14065
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="absolute -bottom-4 -left-4 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full glass-dark text-xs font-bold text-white border border-white/15 shadow-lg"
            >
              <span className="material-symbols-outlined text-[14px] text-brand">directions_boat</span>
              IMO GreenVoyage 2050
            </motion.div>
          </motion.div>
        </div>

        {/* Live data ticker — full width strip */}
        <DataTicker />

        {/* Sensor strip */}
        <div className="max-w-[1280px] mx-auto px-6 py-6 flex flex-wrap items-center gap-x-10 gap-y-4">
          {sensors.map((s, i) => (
            <motion.div
              key={s.label}
              whileHover={{ y: -2 }}
              className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400"
            >
              <motion.span
                className="material-symbols-outlined text-brand"
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
              >
                {s.icon}
              </motion.span>
              {s.label}
            </motion.div>
          ))}
          <div className="ml-auto flex items-center gap-2 text-[11px] text-zinc-400 font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-brand radar-dot" />
            {t("home_extra.console.scroll_hint")}
            <span className="material-symbols-outlined text-[14px]">south</span>
          </div>
        </div>
      </div>
    </header>
  );
}

function What() {
  const { t } = useTranslation();
  const bullets = t("home.what.bullets", { returnObjects: true }) || [];
  return (
    <section className="py-xl bg-surface-container-low">
      <div className="max-w-[1280px] mx-auto px-6 grid md:grid-cols-2 gap-md items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <LiveDashboardMock />
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <h2 className="font-h2 text-h2 text-primary-container mb-6">{t("home.what.title")}</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant mb-4">
            {t("home.what.intro")}
          </p>
          <p className="text-sm text-on-surface-variant mb-8">
            {t("home.what.parent_note")} <strong>United Platform Solutions Global™</strong>.
          </p>
          <ul className="space-y-4">
            {bullets.map((tt) => (
              <li key={tt} className="flex items-start gap-3">
                <span className="material-symbols-outlined icon-fill text-brand mt-1">check_circle</span>
                <div className="font-body-md text-primary">{tt}</div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

function ProblemSolution() {
  const { t } = useTranslation();
  const gaps = [
    { icon: "public_off", title: t("home.scale.gaps.countries_title"), desc: t("home.scale.gaps.countries_desc") },
    { icon: "groups", title: t("home.scale.gaps.people_title"), desc: t("home.scale.gaps.people_desc") },
    { icon: "fact_check", title: t("home.scale.gaps.industry_title"), desc: t("home.scale.gaps.industry_desc") },
  ];

  return (
    <section className="py-xl bg-brand-dark text-white relative overflow-hidden" data-testid="home-scale-challenge">
      <div className="absolute inset-0 tech-grid-dark opacity-40" />
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand/10 blur-3xl pointer-events-none" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="max-w-3xl mb-lg">
          <span className="text-brand text-label-caps">{t("home.scale.eyebrow")}</span>
          <h2 className="font-h2 text-h2 mt-2">
            {t("home.scale.title")} <span className="text-brand">{t("home.scale.title_accent")}</span>
          </h2>
          <p className="font-manrope italic text-zinc-300 mt-3">{t("home.scale.quote")}</p>
          <div className="h-1 w-24 bg-brand mt-4" />
        </div>

        <div className="grid md:grid-cols-2 gap-gutter mb-lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }} className="relative bg-zinc-900/70 backdrop-blur border border-zinc-800 rounded-2xl p-md overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand/10 blur-3xl" />
            <div className="flex items-baseline gap-2">
              <span className="font-manrope font-black text-6xl md:text-7xl text-brand tabular-nums">53B</span>
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">GtCO₂e</span>
            </div>
            <p className="text-zinc-300 mt-2">{t("home.scale.stats.global_label")}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative bg-zinc-900/70 backdrop-blur border border-zinc-800 rounded-2xl p-md overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand/10 blur-3xl" />
            <div className="flex items-baseline gap-2">
              <span className="font-manrope font-black text-6xl md:text-7xl text-brand tabular-nums">2.12B</span>
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">tonnes CO₂</span>
            </div>
            <p className="text-zinc-300 mt-2">{t("home.scale.stats.africa_label")}</p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-gutter mb-lg">
          {gaps.map((g, i) => (
            <motion.div key={g.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-md hover:border-brand/40 transition-colors">
              <span className="material-symbols-outlined text-brand text-3xl mb-3">{g.icon}</span>
              <h4 className="font-manrope font-bold text-white mb-1">{g.title}</h4>
              <p className="text-sm text-zinc-400">{g.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6 }} className="relative rounded-2xl p-md md:p-lg border border-brand/30 bg-gradient-to-br from-brand/15 via-brand/5 to-transparent">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <span className="material-symbols-outlined icon-fill text-brand text-5xl shrink-0">bolt</span>
            <div>
              <div className="text-brand text-label-caps mb-1">{t("home.scale.solution_eyebrow")}</div>
              <p className="font-manrope text-lg md:text-xl text-white leading-snug">{t("home.scale.solution_title")}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const capabilityIcons = ["sensors", "psychology", "task_alt", "public", "hub"];
const capabilityKeys = ["monitoring", "ai", "compliance", "gis", "tenancy"];
const capabilitySpans = ["md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2"];
const capabilityTags = [["LIVE", "24/7"], null, null, null, null];

function Capabilities() {
  const { t } = useTranslation();
  return (
    <section className="py-xl bg-surface">
      <div className="max-w-[1280px] mx-auto px-6">
        <h2 className="font-h2 text-h2 text-primary-container mb-12">{t("home_extra.capabilities.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-gutter">
          {capabilityKeys.map((k, idx) => (
            <motion.div key={k} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: idx * 0.07 }} className={`${capabilitySpans[idx]} bg-white p-md rounded-2xl border border-outline-variant flex flex-col justify-between hover:border-brand transition-all group min-h-[180px]`}>
              <div>
                <span className="material-symbols-outlined text-4xl text-brand mb-4 group-hover:scale-110 transition-transform inline-block">{capabilityIcons[idx]}</span>
                <h4 className="font-h3 text-h3 text-primary mb-2">{t(`home_extra.capabilities.items.${k}.title`)}</h4>
                <p className="text-on-surface-variant">{t(`home_extra.capabilities.items.${k}.desc`)}</p>
              </div>
              {capabilityTags[idx] && (
                <div className="mt-4 flex gap-2">
                  {capabilityTags[idx].map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-surface-container rounded text-[10px] font-bold">{tag}</span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const beneficiaryIcons = ["factory", "account_balance", "diversity_3"];

function Beneficiaries() {
  const { t } = useTranslation();
  const items = t("home_extra.beneficiaries.items", { returnObjects: true }) || [];
  return (
    <section className="py-xl bg-surface-container-low">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="mb-lg">
          <span className="text-brand text-label-caps">{t("home_extra.beneficiaries.eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-primary mt-2">{t("home_extra.beneficiaries.title")}</h2>
          <div className="w-20 h-1 bg-brand mt-sm" />
        </div>
        <div className="grid md:grid-cols-3 gap-gutter">
          {items.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white p-md rounded-2xl border border-outline-variant hover:border-brand hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-tertiary-fixed flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-brand">{beneficiaryIcons[i]}</span>
              </div>
              <h3 className="font-h3 text-h3 text-primary mb-2">{b.title}</h3>
              <p className="text-on-surface-variant text-sm mb-4">{b.desc}</p>
              <ul className="space-y-1.5">
                {(b.bullets || []).map((x) => (
                  <li key={x} className="flex items-center gap-2 text-xs text-on-surface">
                    <span className="material-symbols-outlined icon-fill text-brand text-[14px]">check_circle</span>
                    {x}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PPP() {
  const { t } = useTranslation();
  const bullets = t("home_extra.ppp.bullets", { returnObjects: true }) || [];
  const streams = t("home_extra.ppp.streams", { returnObjects: true }) || [];
  const streamIcons = ["input", "build", "subscriptions", "balance"];
  return (
    <section className="py-xl bg-primary-container relative overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-50" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-lg items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}>
            <span className="text-brand text-label-caps">{t("home_extra.ppp.eyebrow")}</span>
            <h2 className="font-h2 text-h2 text-white mt-2 mb-4">{t("home_extra.ppp.title")}</h2>
            <p className="text-on-primary-container mb-6">{t("home_extra.ppp.intro")}</p>
            <ul className="space-y-3">
              {bullets.map((tt) => (
                <li key={tt} className="flex items-start gap-3 text-zinc-200">
                  <span className="material-symbols-outlined icon-fill text-brand mt-0.5">check_circle</span>
                  <span>{tt}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }} className="grid grid-cols-2 gap-4">
            {streams.map((x, i) => (
              <motion.div key={x.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-zinc-900/70 backdrop-blur p-md rounded-2xl border border-zinc-800 hover:border-brand/40 transition-colors">
                <span className="material-symbols-outlined text-brand text-3xl mb-3">{streamIcons[i]}</span>
                <h4 className="font-manrope font-bold text-white mb-1">{x.title}</h4>
                <p className="text-zinc-400 text-sm">{x.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Strategy() {
  const { t } = useTranslation();
  const phases = [
    { year: t("home.roadmap.year_label_1"), cumulative: "6 countries", active: true, countries: ["Ghana", "Nigeria", "Kenya", "Tanzania", "Uganda", "Morocco"] },
    { year: t("home.roadmap.year_label_3"), cumulative: "10 cumulative", countries: ["Ethiopia", "Ivory Coast", "Guinea", "Zimbabwe"] },
    { year: t("home.roadmap.year_label_4"), cumulative: "14 cumulative", countries: ["Egypt", "South Africa", "Angola", "Algeria"] },
    { year: t("home.roadmap.year_label_5"), cumulative: "20 cumulative", countries: ["Saudi Arabia", "Sudan", "Senegal", "Qatar", "UAE"] },
  ];

  return (
    <section className="py-xl" data-testid="home-roadmap">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="max-w-3xl mb-lg">
          <span className="text-brand text-label-caps">{t("home.roadmap.eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-primary mt-2">{t("home.roadmap.title")}</h2>
          <p className="text-on-surface-variant mt-3 max-w-2xl">{t("home.roadmap.intro")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-gutter relative">
          <div className="hidden md:block absolute top-14 left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-brand via-brand to-brand/10" />
          {phases.map((p, i) => (
            <motion.div
              key={p.year}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.55, delay: i * 0.1 }}
              className={`relative bg-white border rounded-2xl p-md hover:-translate-y-1 transition-all ${
                p.active ? "border-brand shadow-[0_10px_40px_-12px_rgba(144,184,73,0.5)]" : "border-outline-variant hover:border-brand"
              }`}
            >
              <div className={`relative z-10 w-10 h-10 mb-4 rounded-full flex items-center justify-center font-manrope font-extrabold text-xs ${
                p.active ? "bg-brand text-white ring-4 ring-brand/20" : "bg-tertiary-fixed text-brand border border-brand/30"
              }`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-manrope text-xs uppercase tracking-widest text-brand font-bold mb-1">{p.year}</div>
              <div className="font-manrope font-extrabold text-primary text-lg mb-3">{p.cumulative}</div>
              <ul className="space-y-1.5">
                {p.countries.map((country) => (
                  <li key={country} className="flex items-center gap-2 text-sm text-on-surface">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                    {country}
                  </li>
                ))}
              </ul>
              {p.active && (
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-brand text-white px-2 py-0.5 rounded-full font-bold">
                  {t("home.roadmap.now")}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/stats`);
        if (!cancelled) setStats(r.data);
      } catch {
        /* keep null — fallback values render */
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const items = stats
    ? [
        { v: stats.countries, label: t("home_extra.stats.labels.countries") },
        { v: stats.live_programmes, label: t("home_extra.stats.labels.live_programmes") },
        { v: stats.active_devices, suf: "+", label: t("home_extra.stats.labels.devices") },
        { v: stats.partner_companies, suf: "+", label: t("home_extra.stats.labels.companies") },
      ]
    : [
        { v: 0, label: t("home_extra.stats.labels.countries") },
        { v: 0, label: t("home_extra.stats.labels.live_programmes") },
        { v: 0, suf: "+", label: t("home_extra.stats.labels.devices") },
        { v: 0, suf: "+", label: t("home_extra.stats.labels.companies") },
      ];

  return (
    <section className="py-xl relative" data-testid="home-stats">
      <div className="absolute inset-0 z-0">
        <img alt="" className="w-full h-full object-cover opacity-10" src="https://lh3.googleusercontent.com/aida-public/AB6AXuARkEgDHIFXQ2QD_2WBp_aVAcire1MHQgLGFLc6yASkqEvj-rDUwWcdopN3hwiBW0OhMUVN6ynJdzK542PgsYGarowLeJUoxM6F_PGcKhsP0kr15gf8LddqIiUBr46uOI0YZ7G9i-tKSsKaWAYKb5BTMd1K4iJcRVYnTA2j0k1RO3ZAEwxnnDtHxR9MjG3DIX44t3dsMtli4M61nlrAXgvboDQAkf9y-5K7QEJQ22KwKEN8xvAmgeYMEBXQQikcY-NSyNmjTAseqg" />
      </div>
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="flex items-center justify-between mb-md">
          <div>
            <span className="text-brand text-label-caps">{t("home_extra.stats.eyebrow")}</span>
            <h3 className="font-h3 text-h3 text-primary mt-1">{t("home_extra.stats.title")}</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant" data-testid="stats-live-indicator">
            <span className={`w-2 h-2 rounded-full ${stats ? "bg-brand animate-pulse" : "bg-zinc-400"}`} />
            <span className="font-mono">{stats ? t("home_extra.stats.live") : t("home_extra.stats.connecting")}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {items.map((s) => (
            <div key={s.label} className="text-center p-md">
              <div className="text-h1 font-black text-primary-container mb-2 font-manrope">
                <Counter end={s.v} suffix={s.suf || ""} decimals={s.decimals || 0} />
              </div>
              <div className="text-label-caps text-brand">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const { t } = useTranslation();
  return (
    <section className="py-xl bg-brand-dark text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-brand/15 blur-3xl" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-lg items-center">
        <div>
          <span className="text-brand text-label-caps">{t("home_extra.cta.eyebrow")}</span>
          <h2 className="font-h1 text-[34px] md:text-h1 mt-2 mb-4">
            {t("home_extra.cta.title_a")} <span className="text-brand">{t("home_extra.cta.title_accent")}</span>
          </h2>
          <p className="text-zinc-300 max-w-lg">{t("home_extra.cta.intro")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
          <Link to="/contact" className="px-8 py-4 bg-brand text-white font-bold rounded-xl shadow-xl hover:-translate-y-0.5 transition-all text-center">
            {t("home_extra.cta.request_demo")}
          </Link>
          <Link to="/case-studies" className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-all text-center">
            {t("home_extra.cta.see_evidence")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function PlatformPreview() {
  const { t } = useTranslation();
  const bullets = t("home_extra.platform_preview.bullets", { returnObjects: true }) || [];
  return (
    <section className="py-xl bg-background relative overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-30" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-lg items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-brand text-label-caps">{t("home_extra.platform_preview.eyebrow")}</span>
            <h2 className="font-h2 text-h2 text-primary mt-2 mb-4">
              {t("home_extra.platform_preview.title_a")} <br />
              <span className="text-gradient">{t("home_extra.platform_preview.title_b")}</span>
            </h2>
            <p className="text-body-lg text-on-surface-variant max-w-xl mb-6">
              {t("home_extra.platform_preview.intro")}
            </p>
            <ul className="space-y-3">
              {bullets.map((tt) => (
                <li key={tt} className="flex items-start gap-2 text-on-surface">
                  <span className="material-symbols-outlined icon-fill text-brand text-[18px] mt-0.5">check_circle</span>
                  {tt}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="relative aspect-[4/3]"
          >
            <div className="absolute inset-8 rounded-[60%] bg-brand/20 blur-3xl pointer-events-none" />
            <img src="/imagery/kocems-mock-1.png" alt="AOCEMS / KOCEMS dashboard preview" className="relative w-full h-full object-contain drop-shadow-2xl" />
            <motion.div
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-2 -right-2 w-40 md:w-48 aspect-square rounded-2xl overflow-hidden bg-white shadow-xl border border-outline-variant"
            >
              <img src="/imagery/sensor-station.jpg" alt="AOCEMS field sensor station" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-brand text-white text-[9px] font-bold uppercase tracking-widest">
                {t("home_extra.platform_preview.field_hardware")}
              </div>
            </motion.div>
            <div className="absolute top-2 left-2 glass-card px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest text-primary font-bold">
              <span className="text-brand mr-2">●</span> {t("home_extra.platform_preview.live_console")}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <>
      <HeroHome onWatchVideo={() => setVideoOpen(true)} />
      <What />
      <ProblemSolution />
      <Capabilities />
      <PlatformPreview />
      <Beneficiaries />
      <PPP />
      <Strategy />
      <Stats />
      <CtaBanner />
      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        src="/videos/aocems.mp4"
        title={t("home_extra.video_title")}
      />
    </>
  );
}
